import React from 'react';
import LoadingIndicator from 'src/components/LoadingIndicator';
import PaymentMethodForm from 'src/components/PaymentMethodForm';
import SettingsHeader from 'src/components/SettingsHeader';
import emptyImg from 'src/images/empty.svg';
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Icon,
  Input,
  ListGroup,
  ListGroupItem,
  Row,
  toast,
  useApp,
  useStateIn
} from 'straightline-ui';
import { formatPaymentFee } from 'straightline-utils/accounting';
import { PAYMENT_METHOD } from 'straightline-utils/constants';

const paymentMethodIcons = {
  [PAYMENT_METHOD.CARD]: 'credit-card',
  [PAYMENT_METHOD.BANK_ACCOUNT]: 'home'
};

function Billing() {
  const app = useApp();
  const {
    state: { clientSecret, intentLoading },
    assignState
  } = useStateIn({
    clientSecret: null,
    intentLoading: false
  });

  const {
    result: paymentMethods,
    isLoading,
    isError,
    error
  } = app.service('api/payment-methods').useFind({
    query: {
      $limit: 100,
      type: { $in: [PAYMENT_METHOD.CARD, PAYMENT_METHOD.BANK_ACCOUNT] },
      $sort: { created_at: -1 }
    }
  });

  const handlePaymentIntent = async () => {
    assignState({ intentLoading: true });
    try {
      const intent = await app.service('api/payment-methods/setup').create({});
      assignState({ clientSecret: intent.client_secret, intentLoading: false });
    } catch (error) {
      toast.error(
        `There was an issue creating your payment method: ${error.message}`
      );
      assignState({ intentLoading: false });
    }
  };

  const handleSuccess = async (setupIntent) => {
    app
      .service('api/payment-methods')
      .create({
        stripe_id: setupIntent.payment_method
      })
      .then(() => {
        toast.success('Payment method linked successfully');
        app.service('api/payment-methods').invalidateFind();
        assignState({ clientSecret: null });
      })
      .catch((error) => {
        toast.error(
          `There was an issue linking your payment method: ${error.message}`
        );
      });
  };

  const handleCancel = () => {
    assignState({ clientSecret: null });
  };

  const handleError = (error) => {
    toast.error(
      `There was an issue linking your payment method: ${error.message}`
    );
  };

  const setDefaultMethod = (paymentMethod) => {
    toast.confirm({
      title: 'Are you sure?',
      message:
        'Are you sure you want to set this as your default payment method? This method with be used for all future invoices unless you select a different method at checkout.',
      okButtonText: 'Yes, set default',
      cancelButtonText: "Nevermind, don't set",
      handleOnOk: () => {
        return app
          .service('api/payment-methods')
          .patch(paymentMethod.id, {
            default_method: true
          })
          .then(() => {
            toast.success('Default payment method updated successfully');
            app.service('api/payment-methods').invalidateFind();
          })
          .catch((error) => {
            toast.error(
              `There was an issue setting your default payment method: ${error.message}`
            );
          });
      }
    });
  };

  if (isLoading) {
    return (
      <div className="d-flex align-items-center mt-5">
        <LoadingIndicator delay={250} />
      </div>
    );
  }

  if (isError) {
    return (
      <Container>
        <Row className="align-items-center justify-content-center">
          <Col lg="6" className="text-center">
            <img
              src={emptyImg}
              alt="Not Found"
              className="img-fluid animated fadeIn faster mb-5"
            />
            {error.code === 404 ? (
              <h1>Error finding billing information</h1>
            ) : (
              <h1>{error.message}</h1>
            )}
            <Button to="/settings" color="primary" size="lg" className="mr-3">
              <Icon type="settings" className="mr-2" />
              Return to Settings
            </Button>
            <Button to="/support" color="secondary" size="lg">
              <Icon type="send" className="mr-2" />
              Contact Straightline
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Row>
      <Col xl="6">
        <Card>
          <CardBody>
            <p className="lead mb-2">Link new payment method</p>
            <p>
              Securely and instantly link your bank account or credit card to
              Straightline. We never know or see any of your bank credentials or
              balances. By linking your payment accounts, you authorize
              Straightline to debit your account for invoices, shipments,
              accessorials, adjustments, or other amounts owed under your
              agreement with Straightline.
            </p>
            <p className="text-muted mb-0">
              To apply for credit terms, please call us at{' '}
              {process.env.REACT_APP_SUPPORT_PHONE}. We will be happy to discuss
              setting you up on a payment plan that works for your business!
            </p>
            <hr />
            {!clientSecret && (
              <Button
                color="primary"
                block
                className="mt-3"
                onClick={handlePaymentIntent}
                loading={intentLoading}
                disabled={intentLoading}
              >
                Link Payment Method
              </Button>
            )}
            {clientSecret && (
              <PaymentMethodForm
                clientSecret={clientSecret}
                onSuccess={handleSuccess}
                onCancel={handleCancel}
                onError={handleError}
              />
            )}
          </CardBody>
        </Card>
      </Col>
      {paymentMethods.total > 0 && (
        <Col xl="6">
          <Card>
            <CardBody className="pb-0">
              <p className="lead mb-2">Set default payment method</p>
              <p>
                Select your default payment method for paying invoices. You can
                always change this later or select a different method at
                checkout.
              </p>
            </CardBody>
            <ListGroup>
              {paymentMethods?.data.map((paymentMethod) => {
                return (
                  <>
                    <ListGroupItem
                      key={paymentMethod.id}
                      action={true}
                      tag="button"
                      type="button"
                      active={paymentMethod.default_method}
                      className={
                        paymentMethod.default_method
                          ? 'bg-lighter rounded-top-0'
                          : 'rounded-top-0 '
                      }
                      style={{
                        backgroundColor: paymentMethod.default_method
                          ? '#e0edff'
                          : ''
                      }}
                      onClick={() => {
                        if (!paymentMethod.default_method) {
                          setDefaultMethod(paymentMethod);
                        }
                      }}
                      disabled={isLoading}
                    >
                      <Row className="align-items-start">
                        <Col xs="auto" className="pr-0">
                          <Icon
                            className="text-secondary"
                            type={
                              paymentMethodIcons[paymentMethod.type] || 'edit-3'
                            }
                            style={{ fontSize: 35, lineHeight: 1 }}
                          />
                        </Col>
                        <Col>
                          <Row className="d-flex justify-content-between align-items-start">
                            <Col>
                              <div className="text-body">
                                {paymentMethod.description} ending in{' '}
                                {paymentMethod.reference_number}
                              </div>
                              <div className="text-secondary">
                                {formatPaymentFee(paymentMethod)}
                              </div>
                              <div className="text-secondary">
                                Expires:{' '}
                                {paymentMethod.exp_month
                                  ? `${paymentMethod.exp_month}/${paymentMethod.exp_year}`
                                  : 'Never'}
                              </div>
                            </Col>
                            <Col sm="auto">
                              <Input
                                type="radio"
                                className="mr-n2"
                                checked={
                                  paymentMethod.default_method ? true : false
                                }
                              />
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </ListGroupItem>
                  </>
                );
              })}
            </ListGroup>
          </Card>
        </Col>
      )}
    </Row>
  );
}

export default (routerProps) => {
  return (
    <Container fluid>
      <SettingsHeader activeTab="billing" />
      <Billing {...routerProps} />
    </Container>
  );
};
